import { Head, router, usePage } from '@inertiajs/react'
import { FormEvent, useMemo, useState } from 'react'
import {
  HiOutlineBars3,
  HiOutlineBuildingOffice2,
  HiOutlineCalendarDays,
  HiOutlineChatBubbleLeftEllipsis,
  HiOutlineCheck,
  HiOutlineEnvelope,
  HiOutlinePencil,
  HiOutlinePhone,
  HiOutlineTag,
  HiOutlineTrash,
  HiOutlineTrophy,
  HiOutlineUser,
  HiOutlineUserPlus,
  HiOutlineArrowPath,
} from 'react-icons/hi2'
import Avatar from '../../components/Avatar'
import ConfirmDialog from '../../components/ConfirmDialog'
import LeadAccordionHeader, {
  type LeadHeaderData,
  type LeadStage,
} from '../../components/LeadAccordionHeader'
import PageSubHeader from '../../components/PageSubHeader'
import TopBar from '../../components/TopBar'
import type { LeadPriority } from '../../components/PriorityBadge'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

type Actor = { id: number; name: string }
type ActivityType =
  | 'lead_created'
  | 'lead_updated'
  | 'stage_changed'
  | 'won_marked'
  | 'assignee_changed'
  | 'field_changed'
  | 'note_added'
  | 'note_edited'
  | 'note_deleted'

type Activity = {
  id: number
  type: ActivityType
  actor: Actor | null
  actor_kind: 'user' | 'form' | 'admin' | 'system'
  payload: Record<string, unknown>
  created_at: string
}

type Note = {
  id: number
  body: string
  author: Actor
  created_at: string
  can_edit: boolean
}

type FocusedLead = {
  id: number
  name: string
  email: string
  phone: string | null
  company: string
  priority: LeadPriority
  stage: LeadStage
  won: boolean
  initials: string
  created_at_display: string
  assignee: Actor | null
  custom_contact_values: Array<{ key: string; label: string; value: string }>
  question_answers: Array<{ step_id: number; step_text: string; option_labels: string[] }>
  notes: Note[]
  activities: Activity[]
}

type Sibling = {
  id: number
  name: string
  email: string
  phone: string | null
  company: string
  priority: LeadPriority
  stage: LeadStage
  initials: string
}

type CurrentUser = {
  id: number
  name: string
  email: string
  role: string
  initials: string | null
  photo_url: string | null
}

type Props = {
  event: { id: number; slug: string; title: string }
  focused_lead: FocusedLead
  siblings: Sibling[]
  company_count: number
}

// ────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────

export default function Show({ event, focused_lead, siblings, company_count }: Props) {
  const { props: shared } = usePage<{ current_user: CurrentUser | null }>()
  const user = shared.current_user
  if (!user) return null

  return (
    <>
      <Head title={`${focused_lead.name} · ${focused_lead.company}`} />
      <div className="min-h-screen bg-ce-off-white font-sans">
        <TopBar currentUser={user} />

        {/* Sub-header: back button + company name + count */}
        <PageSubHeader
          backHref={`/events/${event.slug}/leads`}
          backLabel="Back to leads"
          title={`${focused_lead.company} (${company_count})`}
        />

        {/* Accordion stack */}
        <div className="mx-auto w-full max-w-[1440px] px-5 py-8 md:px-10 md:py-10">
          <div className="flex flex-col gap-5">
            {/* Focused (expanded) accordion */}
            <div className="overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
              <div className="flex flex-col gap-8 px-6 py-5">
                <div>
                  <LeadAccordionHeader lead={toHeaderData(focused_lead)} />
                  <div className="mt-5 h-px w-full bg-gray-200" />
                </div>

                <PersonalDetailsGrid lead={focused_lead} />

                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="lg:w-[603px] lg:shrink-0">
                    <AddNotesCard leadId={focused_lead.id} />
                  </div>
                  <div className="flex-1">
                    <ActivityLogCard
                      leadId={focused_lead.id}
                      activities={focused_lead.activities}
                      notes={focused_lead.notes}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sibling (collapsed) accordions */}
            {siblings.map((s) => (
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                onClick={() => router.visit(`/leads/${s.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    router.visit(`/leads/${s.id}`)
                  }
                }}
                className="cursor-pointer overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)] transition hover:shadow-md"
              >
                <div className="px-6 py-5">
                  <LeadAccordionHeader lead={toHeaderData(s)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// Subcomponents (inline — single caller)
// ────────────────────────────────────────────────────────────────

function toHeaderData(lead: FocusedLead | Sibling): LeadHeaderData {
  return {
    id: lead.id,
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    priority: lead.priority,
    stage: lead.stage,
    initials: lead.initials,
  }
}

function PersonalDetailsGrid({ lead }: { lead: FocusedLead }) {
  const [open, setOpen] = useState(true)

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-4"
      >
        <span className="font-display text-lg font-medium tracking-tight text-ink">
          Personal Details
        </span>
        <HiOutlineBars3
          className={`h-4 w-4 text-gray-500 transition ${open ? '' : '-rotate-90'}`}
        />
      </button>

      {open && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-3">
          <DetailCell
            icon={<HiOutlineUser className="h-5 w-5" />}
            label="Name"
            value={lead.name}
          />
          <DetailCell
            icon={<HiOutlineEnvelope className="h-5 w-5" />}
            label="Email"
            value={lead.email}
          />
          <DetailCell
            icon={<HiOutlinePhone className="h-5 w-5" />}
            label="Phone"
            value={lead.phone || '—'}
          />
          <DetailCell
            icon={<HiOutlineBuildingOffice2 className="h-5 w-5" />}
            label="Company"
            value={lead.company}
          />
          <DetailCell
            icon={<HiOutlineCalendarDays className="h-5 w-5" />}
            label="Date Created"
            value={lead.created_at_display}
          />
          <AssignedToCell assignee={lead.assignee} />

          {/* Custom contact fields (non-system) */}
          {lead.custom_contact_values.map((v) => (
            <DetailCell
              key={`contact-${v.key}`}
              icon={<HiOutlineTag className="h-5 w-5" />}
              label={v.label}
              value={v.value}
            />
          ))}

          {/* Question answers */}
          {lead.question_answers.map((q) => (
            <DetailCell
              key={`q-${q.step_id}`}
              icon={<HiOutlineChatBubbleLeftEllipsis className="h-5 w-5" />}
              label={q.step_text}
              value={q.option_labels.join(', ')}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DetailCell({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-[22px]">
      <span className="mt-0.5 shrink-0 text-gray-500">{icon}</span>
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-display text-sm leading-5 text-black/40">{label}</p>
        <p className="font-display text-sm leading-5 text-ink">{value}</p>
      </div>
    </div>
  )
}

function AssignedToCell({ assignee }: { assignee: Actor | null }) {
  return (
    <div className="flex items-start gap-3">
      {assignee ? (
        <Avatar id={assignee.id} name={assignee.name} size="md" />
      ) : (
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500">
          <HiOutlineUser className="h-4 w-4" />
        </span>
      )}
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-display text-sm leading-5 text-black/40">Assigned to</p>
        <p className="font-display text-sm leading-5 text-ink">
          {assignee?.name || 'Unassigned'}
        </p>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// Add Notes card
// ────────────────────────────────────────────────────────────────

function AddNotesCard({ leadId }: { leadId: number }) {
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = body.trim()
    if (!trimmed || submitting) return

    setSubmitting(true)
    router.post(
      `/leads/${leadId}/notes`,
      { note: { body: trimmed } },
      {
        preserveScroll: true,
        onSuccess: () => setBody(''),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-full flex-col gap-5 rounded-xl border border-gray-200 bg-white px-6 py-5"
    >
      <h2 className="font-display text-lg font-medium tracking-tight text-ink">Add Notes</h2>
      <p className="font-display text-sm leading-5 text-ink">
        Use notes to record key discussion points from meetings and add reminders.
      </p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Enter your note here..."
        rows={4}
        className="min-h-[93px] resize-y rounded-md border border-gray-300 bg-black/[0.03] px-3 py-2 font-display text-sm text-ink shadow-sm placeholder:text-gray-500 focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
      />
      <div>
        <button
          type="submit"
          disabled={!body.trim() || submitting}
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add Note
        </button>
      </div>
    </form>
  )
}

// ────────────────────────────────────────────────────────────────
// Activity Log card
// ────────────────────────────────────────────────────────────────

type TimelineItem =
  | { kind: 'note'; id: number; created_at: string; note: Note }
  | { kind: 'activity'; id: number; created_at: string; activity: Activity }

function ActivityLogCard({
  leadId,
  activities,
  notes,
}: {
  leadId: number
  activities: Activity[]
  notes: Note[]
}) {
  const items = useMemo(() => buildTimeline(activities, notes), [activities, notes])
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<number | null>(null)

  const handleDeleteConfirm = () => {
    if (pendingDeleteNoteId == null) return
    router.delete(`/leads/${leadId}/notes/${pendingDeleteNoteId}`, {
      preserveScroll: true,
      onFinish: () => setPendingDeleteNoteId(null),
    })
  }

  return (
    <div className="flex h-full min-h-[297px] flex-col gap-5 rounded-xl border border-gray-200 bg-white px-6 py-5">
      <h2 className="font-display text-lg font-medium tracking-tight text-ink">Activity Log</h2>
      {items.length === 0 ? (
        <p className="font-display text-sm text-gray-500">No activity yet.</p>
      ) : (
        <ol className="relative flex flex-col gap-4">
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1
            return (
              <li key={`${item.kind}-${item.id}`} className="relative flex gap-3">
                {!isLast && (
                  <span
                    aria-hidden="true"
                    className="absolute left-[15px] top-8 h-[calc(100%+4px)] w-0.5 bg-gray-200"
                  />
                )}
                <TimelineIcon item={item} />
                <div className="flex-1 pt-1">
                  {item.kind === 'note' ? (
                    <NoteTimelineRow
                      leadId={leadId}
                      note={item.note}
                      onRequestDelete={() => setPendingDeleteNoteId(item.note.id)}
                    />
                  ) : (
                    <ActivityTimelineRow activity={item.activity} />
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}

      <ConfirmDialog
        open={pendingDeleteNoteId !== null}
        tone="danger"
        title="Delete this note?"
        body="This cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onClose={() => setPendingDeleteNoteId(null)}
      />
    </div>
  )
}

// Merge activities and notes into a single desc-sorted timeline.
// Drop rows we don't want to show:
//   - field_changed (audit-only)
//   - note_edited / note_deleted (folded — current note is rendered live)
//   - note_added whose referenced note no longer exists (deleted)
function buildTimeline(activities: Activity[], notes: Note[]): TimelineItem[] {
  const notesById = new Map(notes.map((n) => [n.id, n]))

  const activityItems: TimelineItem[] = activities
    .filter((a) => a.type !== 'field_changed' && a.type !== 'note_edited' && a.type !== 'note_deleted')
    .filter((a) => {
      if (a.type !== 'note_added') return true
      const noteId = a.payload?.lead_note_id as number | undefined
      return noteId != null && notesById.has(noteId)
    })
    .map((a) => {
      if (a.type === 'note_added') {
        const noteId = a.payload.lead_note_id as number
        const note = notesById.get(noteId)!
        return {
          kind: 'note',
          id: note.id,
          created_at: note.created_at, // use note's own timestamp
          note,
        } satisfies TimelineItem
      }
      return {
        kind: 'activity',
        id: a.id,
        created_at: a.created_at,
        activity: a,
      } satisfies TimelineItem
    })

  return activityItems.sort((a, b) => b.created_at.localeCompare(a.created_at))
}

function TimelineIcon({ item }: { item: TimelineItem }) {
  let bg = '#6B7280'
  let icon: React.ReactNode = <HiOutlineUser className="h-5 w-5" />

  if (item.kind === 'note') {
    bg = '#004F51'
    icon = <HiOutlineBars3 className="h-5 w-5" />
  } else {
    switch (item.activity.type) {
      case 'lead_created':
        bg = '#9CA3AF'
        icon = <HiOutlineUserPlus className="h-5 w-5" />
        break
      case 'stage_changed':
        bg = '#93D500'
        icon = <HiOutlineCheck className="h-5 w-5" />
        break
      case 'won_marked':
        bg = '#F59E0B'
        icon = <HiOutlineTrophy className="h-5 w-5" />
        break
      case 'assignee_changed':
        bg = '#00BED6'
        icon = <HiOutlineUser className="h-5 w-5" />
        break
      case 'lead_updated':
        bg = '#6B7280'
        icon = <HiOutlineArrowPath className="h-5 w-5" />
        break
    }
  }

  return (
    <span
      className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ring-[6px] ring-white"
      style={{ backgroundColor: bg }}
    >
      {icon}
    </span>
  )
}

function NoteTimelineRow({
  leadId,
  note,
  onRequestDelete,
}: {
  leadId: number
  note: Note
  onRequestDelete: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note.body)
  const [submitting, setSubmitting] = useState(false)

  const save = () => {
    const trimmed = draft.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    router.patch(
      `/leads/${leadId}/notes/${note.id}`,
      { note: { body: trimmed } },
      {
        preserveScroll: true,
        onSuccess: () => setEditing(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="font-display text-sm leading-5 text-ink">Notes</p>
      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="resize-y rounded-md border border-gray-300 bg-white px-3 py-2 font-display text-sm text-ink shadow-sm focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={!draft.trim() || submitting}
              className="rounded-md bg-ink px-3 py-1 font-display text-sm font-medium text-white transition hover:bg-midnight-green disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(note.body)
                setEditing(false)
              }}
              className="rounded-md border border-gray-300 bg-white px-3 py-1 font-display text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="font-display text-sm leading-5 text-black/60">{note.body}</p>
      )}
      <p className="font-display text-xs font-medium leading-5 text-black/40">
        {formatTime(note.created_at)}
      </p>
      {note.can_edit && !editing && (
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 font-display text-sm font-medium text-ink transition hover:underline"
          >
            <HiOutlinePencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <span className="h-1 w-1 rounded-full bg-gray-300" />
          <button
            type="button"
            onClick={onRequestDelete}
            className="inline-flex items-center gap-1 font-display text-sm font-medium text-rose-600 transition hover:underline"
          >
            <HiOutlineTrash className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

function ActivityTimelineRow({ activity }: { activity: Activity }) {
  const { title, detail } = describeActivity(activity)
  return (
    <div className="flex flex-col gap-1">
      <p className="font-display text-sm leading-5 text-ink">{title}</p>
      {detail && (
        <p className="font-display text-sm leading-5 text-black/60">{detail}</p>
      )}
      <p className="font-display text-xs font-medium leading-5 text-black/40">
        {formatTime(activity.created_at)}
        {activityActorLabel(activity) && ` · ${activityActorLabel(activity)}`}
      </p>
    </div>
  )
}

function describeActivity(a: Activity): { title: string; detail?: string } {
  switch (a.type) {
    case 'lead_created':
      return { title: 'Lead Created' }
    case 'lead_updated':
      return { title: 'Re-engaged via form' }
    case 'stage_changed': {
      const to = (a.payload?.to as string | undefined) || ''
      return { title: stagePastLabel(to) || 'Stage Changed' }
    }
    case 'won_marked': {
      const to = a.payload?.to
      return { title: to ? 'Marked as Won' : 'Unmarked as Won' }
    }
    case 'assignee_changed':
      return { title: 'Assignee Changed' }
    default:
      return { title: a.type }
  }
}

function stagePastLabel(stage: string): string | null {
  switch (stage) {
    case 'prospect':
      return 'Moved to Prospect'
    case 'in_progress':
      return 'In Progress'
    case 'proposal_sent':
      return 'Proposal Sent'
    default:
      return null
  }
}

function activityActorLabel(a: Activity): string | null {
  if (a.actor) return a.actor.name
  switch (a.actor_kind) {
    case 'form':
      return 'Self-service kiosk'
    case 'system':
      return 'System'
    case 'admin':
      return 'Admin'
    default:
      return null
  }
}

// Relative for <24h, absolute after.
function formatTime(iso: string): string {
  const date = new Date(iso)
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  return date.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
